import { clsx } from "clsx";
import { type ReactElement, type ReactNode, forwardRef, useEffect, useRef, useState } from "react";
import type { Props } from "react-select";
import Select from "react-select";
import Async, { type AsyncProps } from "react-select/async";
import Creatable, { type CreatableProps } from "react-select/creatable";
import WindowedSelect from "react-windowed-select";
import { type BaseFieldProps, Field, type WrappedFieldProps } from "redux-form";

import { fieldIsInWarnState, fieldIsInvalid } from "@/app-utils/form.ts";
import { FormError } from "@/ui-modules/core/form/layout/FormError.js";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.js";
import { FormInputWidget } from "@/ui-modules/core/form/layout/FormInputWidget.js";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.js";
import { FormNote } from "@/ui-modules/core/form/layout/FormNote.js";
import { FormWarning } from "@/ui-modules/core/form/layout/FormWarning.js";

type ReactSelectOptionType = any;

export type SelectProps = Props<ReactSelectOptionType, any, any> &
  Partial<AsyncProps<ReactSelectOptionType, any, any>> &
  CreatableProps<ReactSelectOptionType, any, any>;

export interface IReactSelectFieldProps extends Partial<WrappedFieldProps>, SelectProps {
  className?: string;
  formGroupClassName?: string;
  label?: string | ReactElement;
  customStyles?: any;

  forceWarnState?: string | ReactElement;
  info?: string | ReactElement;
  hotspotId?: string;
  tooltip?: string;

  handleChange?: (value: any) => void;
  reference?: (ref) => any;
  horizontalLayout?: boolean;

  additionalButton?: ReactNode;

  async?: boolean;
  creatable?: boolean;
  windowed?: boolean; // windowing for large lists TODO if needed use (1) react-windowed-select like in DataSeriesSelector, but no support for async (2) react-select-virtualized with support of async but currently broken compatibility with react-select v3
}

export const HORIZONTAL_LABEL_FORM_INPUT_CLASS = "col-sm-2";
export const HORIZONTAL_CONTENT_FORM_INPUT_CLASS = "col-sm-10";
export const HORIZONTAL_CONTENT_FORM_INPUT_CLASS_WITHOUT_LABEL = "col-sm-12";

function searchParentNodeFor(
  element: Node,
  predicate: (arg0: Node | HTMLElement) => boolean,
  boundary: HTMLElement = document.body,
): boolean {
  let currentElement: Node | null = element;

  while ((currentElement = currentElement.parentNode) !== null && currentElement !== boundary) {
    if (typeof predicate === "function" && predicate(currentElement)) {
      return true;
    }
  }

  return false;
}

/**
 * React Select field to be used in context of redux-form
 * @see https://react-select.com/props
 */
export default forwardRef(function ReactSelectField(props: IReactSelectFieldProps, ref) {
  const selectClasses = clsx(`react-select full-width select2-offscreen`, {
    [props.className]: !!props.className,
    error: fieldIsInvalid(props),
    warning: !!props.forceWarnState || fieldIsInWarnState(props),
  });

  // Set default. Avoid to override props with parent props and to list manually all available options
  // Can be dangerous though if the Select API changes, like having title for label for instance
  // but at least it stays flexible enough and more future-proof
  // TODO: isolate the "default" props of ReactSelect in its own component (this one is useful for redux-form and form layout)
  //      and make use of it everywhere (including here) e.g. AdvancedSearchSelect in order to maximize the consistency
  const defaultProps = {
    classNamePrefix: "react-select",
    // eslint-disable-next-line no-prototype-builtins
    getOptionValue: (option: any) => (option.hasOwnProperty("value") ? option.value : option.id),
    closeMenuOnSelect: true,
    backspaceRemovesValue: false,
    loadingMessage: () => "Loading...",
    noOptionsMessage: () => "No options",
    menuShouldScrollIntoView: true,
    maxMenuHeight: 180,
    // async
    cacheOptions: false,
    defaultOptions: true, // tells the control to immediately fire the remote request
    loadOptions: () => undefined, // mandatory for async
  };
  const [uiProps, setUiProps] = useState<any>({
    menuPlacement: "auto",
    // menuPosition: "fixed",
    menuPortalTarget: document.body,
    styles: {
      ...props.customStyles,
      menuPortal: (provided) => ({ ...provided, ...props.customStyles?.menuPortal, zIndex: 9999 }),
      menu: (styles) => ({ ...styles, width: "max-content", minWidth: "100%" }), // allows the dropdown to use the full width driven by the content
    },
  });

  const overriddenProps = {
    ref:
      ref ||
      ((ref) => {
        if (props.reference && typeof props.reference === "function") {
          props.reference(ref);
        }
      }),
    options: props.options || [],
    value: props.input.value,
    className: selectClasses,
    onChange: (value) => {
      props.input.onChange(value);
      if (props.handleChange) {
        props.handleChange(value);
      }
    },
    onBlur: () => props.input.onBlur(props.input.value),
  };

  const componentProps = Object.assign({}, defaultProps, uiProps, props, overriddenProps);
  const selfRef = useRef<HTMLDivElement>();

  // const { a, b, c, ...rest } to extract properties not related to Async/Creatable/Select...
  const { hotspotId, label, tooltip, async, creatable, windowed, input, meta, forceWarnState, info, ...selectProps } =
    componentProps;

  // TODO Better solution than CSS position
  // selectProps = {
  //   ...selectProps,
  //   menuPortalTarget: document.body,
  //   styles: {
  //     ...selectProps.styles,
  //     menuPortal: (base) => ({
  //       ...base,
  //       zIndex: 650,
  //     }),
  //   },
  // };

  useEffect(() => {
    /**
     * @FIXME This is a BAD hack to setup correctly React-Select depending on the type of the parent where it is rendered.
     *        If any class names of the parent changes, this hack will fail!
     *        But at least we don't have to configure it everywhere
     */
    const isInPopOver = searchParentNodeFor(
      selfRef.current,
      (el: HTMLElement) => el.classList.contains("popover") || el.getAttribute("role") === "tooltip",
    );

    if (isInPopOver) {
      setUiProps({
        menuPlacement: "auto",
        menuPosition: "fixed",
        styles: {
          ...props.customStyles,
          menuPortal: (provided) => ({ ...provided, ...props.customStyles?.menuPortal, zIndex: 9999 }),
        },
      });
    }
  }, []);

  return (
    <FormInputGroup horizontalLayout={props.horizontalLayout}>
      {props.label && <FormLabel hotspotId={hotspotId}>{props.label}</FormLabel>}

      <FormInputWidget tooltip={tooltip}>
        <div
          style={{ display: "flex", flexDirection: "row", alignItems: "center", columnGap: "12px", flex: 1 }}
          ref={selfRef}
        >
          {async ? (
            <Async {...selectProps} />
          ) : creatable ? (
            <Creatable {...selectProps} />
          ) : windowed || props.options?.length > 500 ? (
            <WindowedSelect {...selectProps} />
          ) : (
            <Select {...selectProps} />
          )}
          {props.additionalButton}
        </div>
        {fieldIsInvalid(props) && <FormError>{props.meta.error}</FormError>}
        {(!!forceWarnState || fieldIsInWarnState(props)) && !fieldIsInvalid(props) && (
          <FormWarning>{forceWarnState || meta.warning}</FormWarning>
        )}
        {!!info && <FormNote>{info}</FormNote>}
      </FormInputWidget>
    </FormInputGroup>
  );
});

export const ReactSelectReduxFormField = forwardRef(function ReactSelectReduxFormField(
  props: IReactSelectFieldProps & BaseFieldProps,
  ref,
) {
  return <Field {...props} ref={ref as any} />;
});
